import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { deepMerge } from './common.util';

/**
 * 城市
 */
export interface City {
  /** 经度 */
  longitude: number;
  /** 纬度 */
  latitude: number;
  /** 名称 */
  name: string;
}

/**
 * 业务线
 */
export interface BizLine {
  /** 起点 */
  from: string;
  /** 终点 */
  to: string[];
}

/**
 * 飞线配置
 */
export interface FlylineOptions {
  /** 地球半径 */
  earthRadius: number;
  /** 线条配置 */
  line?: {
    /** 线条宽度 */
    width?: number;
    /** 每条曲线点数 */
    pointsPerLine?: number;
    /** 线条渐变颜色 */
    colors?: {
      /** 起始颜色 - 支持传入 THREE.Color 或 十六进制数值 */
      start?: THREE.Color | number;
      /** 结束颜色 - 支持传入 THREE.Color 或 十六进制数值 */
      end?: THREE.Color | number;
    };
    /** 线条透明度 */
    opacity?: number;
  };
  /** 动画配置 */
  animation?: {
    /** 绘制速度 (调低以使动画更慢) */
    drawSpeed?: number;
    /** 线条之间的延迟时间 (ms) */
    delayBetweenLines?: number;
    /** 重新开始前的延迟时间 (ms) */
    delayBeforeRestart?: number;
  };
  /** 城市列表 */
  cityList: Record<string, City>;
  /** 业务线列表 */
  bizLines: Record<string, BizLine>;
}

export class Flyline {
  /** 核心属性 */
  private readonly scene: THREE.Scene;
  /** 配置 */
  private readonly options: Required<FlylineOptions>;

  /** 线条组 */
  private readonly linesGroup: THREE.Group;
  /** 曲线 */
  private readonly curves: THREE.CubicBezierCurve3[];

  /** 动画相关 */
  private animationId: number | null;
  /** 曲线参数 */
  private static readonly CURVE_PARAMS = {
    /**
     * 角度因子: 控制整体弧度
     * 180 / Math.PI / 2 = 28.64788975654116
     */
    ANGLE_FACTOR: 180 / Math.PI / 2,
    /** 高度因子: 控制弧线最高点 */
    HEIGHT_FACTOR: 12,
    /** 长度因子: 控制控制点距离 */
    LENGTH_FACTOR: 0.4
  };

  /** 当前正在绘制的线条索引 */
  private currentLineIndex: number = 0;
  /** 当前线条的绘制进度 */
  private currentDrawProgress: number = 0;

  /** 默认配置 */
  static readonly DEFAULT_OPTIONS: Required<FlylineOptions> = {
    earthRadius: 19.5,
    line: {
      width: 2,
      pointsPerLine: 100,
      colors: {
        start: new THREE.Color(0x4facfe),
        end: new THREE.Color(0x00f2fe)
      },
      opacity: 1
    },
    animation: {
      drawSpeed: 0.01,
      delayBetweenLines: 300,
      delayBeforeRestart: 1000
    },
    cityList: {},
    bizLines: {}
  };

  /** 每条线的当前长度 */
  private lineLengths: number[] = [];

  /**
   * 构造函数
   * @param scene - 场景
   * @param options - 配置
   */
  constructor(scene: THREE.Scene, options?: Partial<FlylineOptions>) {
    this.scene = scene;
    // 使用深度合并替换浅合并
    // @ts-ignore
    this.options = deepMerge(Flyline.DEFAULT_OPTIONS, options);

    this.linesGroup = new THREE.Group();
    this.curves = [];
    this.animationId = null;
  }

  /**
   * 初始化
   */
  public init(): void {
    this.createAllLines();
    this.addToScene();
    this.startAnimation();
  }

  /**
   * 添加到场景
   */
  private addToScene(): void {
    this.scene.add(this.linesGroup);
  }

  /**
   * 创建所有线条
   */
  private createAllLines(): void {
    const { bizLines, cityList } = this.options;

    Object.values(bizLines).forEach((bizLine: BizLine) => {
      const fromPoint = this.getCityPosition(cityList[bizLine.from]);

      bizLine.to.forEach((toCity: string) => {
        const toPoint = this.getCityPosition(cityList[toCity]);

        this.createLine(fromPoint, toPoint);
      });
    });
  }

  /**
   * 获取城市位置
   * @param city 城市
   * @returns 位置
   */
  private getCityPosition(city: City): THREE.Vector3 {
    return this.lonLatToXYZ(
      this.options.earthRadius,
      city.longitude,
      city.latitude
    );
  }

  /**
   * 创建线条
   * @param fromPoint 起点
   * @param toPoint 终点
   */
  private createLine(fromPoint: THREE.Vector3, toPoint: THREE.Vector3): void {
    // 创建曲线和线条网格
    const { curve, lineMesh } = this.createCurvedLine(fromPoint, toPoint);
    // 将曲线添加到曲线数组中
    this.curves.push(curve);
    // 设置线条初始可见长度为0
    lineMesh.geometry.setDrawRange(0, 0);
    // 初始化线条长度为0
    this.lineLengths.push(0);
    // 将线条网格添加到线条组中
    this.linesGroup.add(lineMesh);
  }

  /**
   * 创建曲线
   * @param startPoint 起点
   * @param endPoint 终点
   * @returns 曲线和线条网格
   */
  private createCurvedLine(startPoint: THREE.Vector3, endPoint: THREE.Vector3) {
    // 计算曲线和控制点
    const { curve } = this.calculateCurvePoints(startPoint, endPoint);
    // 创建线条网格
    const lineMesh = this.createLineMesh(curve);
    // 返回曲线和线条网格
    return { curve, lineMesh };
  }

  /**
   * 计算曲线点
   * @param startPoint 起点
   * @param endPoint 终点
   * @returns 曲线和控制点
   */
  private calculateCurvePoints(startPoint: THREE.Vector3, endPoint: THREE.Vector3) {
    // 曲线参数
    const { ANGLE_FACTOR, HEIGHT_FACTOR, LENGTH_FACTOR } = Flyline.CURVE_PARAMS;
    // 计算向量夹角 -> 控制飞线的弧度
    const angle = startPoint.angleTo(endPoint) * ANGLE_FACTOR;

    // 计算控制点 -> 控制飞线的弧度
    const controlPoints = this.calculateControlPoints(
      startPoint,
      endPoint,
      angle * angle * HEIGHT_FACTOR,
      angle * LENGTH_FACTOR
    );

    // 计算曲线 -> 创建三次贝塞尔曲线
    const curve = new THREE.CubicBezierCurve3(
      startPoint,
      controlPoints.control1,
      controlPoints.control2,
      endPoint
    );

    return { curve, controlPoints };
  }

  /**
   * 计算控制点
   * @param start 起点
   * @param end 终点
   * @param height 高度
   * @param length 长度
   * @returns 控制点
   */
  private calculateControlPoints(start: THREE.Vector3, end: THREE.Vector3, height: number, length: number) {
    // 中心点
    const center = new THREE.Vector3(0, 0, 0);
    // 向量中心
    const normal = this.getVectorCenter(start.clone(), end.clone());
    /**
     * 绘制法线 - [射线](https://threejs.org/docs/#api/zh/math/Ray)
     *  - 由一个原点向一个确定的方向发射。
     *  - 它被 RayCaster（光线投射）所使用，以用于辅助 raycasting。光线投射用于在各个物体之间进行拾取（当鼠标经过三维空间中的物体 / 对象时进行拾取）。
     *
     * Ray( origin : Vector3, direction : Vector3 )
     *  - origin - （可选）Ray（射线）的原点，默认值是一个位于 (0, 0, 0) 的 Vector3。
     *  - direction - Vector3 Ray（射线）的方向。该向量必须经过标准化（使用 Vector3.normalize），这样才能使方法正常运行。默认值是一个位于 (0, 0, -1) 的 Vector3。
     */
    const ray = new THREE.Ray(center, normal);
    /**
     * 计算顶点:
     *
     * 计算贝塞尔曲线的顶点位置：
     *  - new THREE.Ray(center, normal)
     *
     * 创建一条射线
     *  - 从中心点(center)出发，沿着法线方向(normal)延伸
     *  - at(height / normal.length(), new THREE.Vector3())
     *  - height 是曲线的最大高度
     *  - normal.length() 是法线向量的长度
     *  - height / normal.length() 决定了点在射线上的距离
     *  - 返回射线上的一个点的位置
     *
     * 形象地说：这个顶点将用于控制飞线的弧度：
     *  - 顶点越高，飞线的弧度越大
     *  - 顶点越低，飞线就越平
     *  - 这样可以让飞线看起来像一个优美的弧形，而不是直线
     *  - 这是创建飞线曲线的关键步骤，它决定了飞线的形状和高度。
     */
    const topPoint = ray.at(
      /* 曲线的最大高度 / 法线向量的长度 = 点在射线上的距离 */
      height / normal.length(),
      /* 返回射线上的一个点的位置 */
      new THREE.Vector3()
    );

    return {
      control1: this.getProportionalPoint(start.clone(), topPoint, length),
      control2: this.getProportionalPoint(end.clone(), topPoint, length)
    };
  }

  /**
   * 创建线条网格
   * @param curve 曲线
   * @returns 线条网格
   */
  private createLineMesh(curve: THREE.CubicBezierCurve3): Line2 {
    /** 创建线条几何体 */
    const geometry = this.createLineGeometry(curve);
    /** 创建线条材质 */
    const material = this.createLineMaterial();
    /** 创建线条 */
    return new Line2(geometry, material);
  }

  /**
   * 创建线条几何体
   * @param curve 曲线
   * @returns 几何体
   */
  private createLineGeometry(curve: THREE.CubicBezierCurve3): LineGeometry {
    const geometry = new LineGeometry();
    const { positions, colors } = this.calculateLineAttributes(curve);

    geometry.setPositions(positions);
    geometry.setColors(colors);
    // 初始设置为不可见
    geometry.setDrawRange(0, 0);

    return geometry;
  }

  /**
   * 计算线条属性
   * @param curve 曲线
   * @returns 位置和颜色
   */
  private calculateLineAttributes(curve: THREE.CubicBezierCurve3) {
    const points = curve.getPoints(this.options.line.pointsPerLine);
    const positions: number[] = [];
    const colors: number[] = [];

    // 获取颜色配置
    const { start, end } = this.options.line.colors || {
      start: new THREE.Color(0x4facfe),
      end: new THREE.Color(0x00f2fe)
    };

    // 转换颜色为 THREE.Color 对象
    const startColor = start instanceof THREE.Color ? start : new THREE.Color(start);
    const endColor = end instanceof THREE.Color ? end : new THREE.Color(end);

    points.forEach((point, i) => {
      // 使用平滑的渐变过渡
      const t = i / ((this.options.line.pointsPerLine || 100) - 1);
      const color = new THREE.Color().lerpColors(startColor, endColor, t);

      // 增加颜色亮度
      color.multiplyScalar(1.5);
      colors.push(color.r, color.g, color.b);
      positions.push(point.x, point.y, point.z);
    });

    return { positions, colors };
  }

  /**
   * 创建线条材质
   */
  private createLineMaterial(): LineMaterial {
    return new LineMaterial({
      linewidth: this.options.line.width,
      vertexColors: true,
      dashed: false,
      transparent: true,
      opacity: this.options.line.opacity,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight)
    });
  }

  /**
   * 动画循环
   */
  private animate(): void {
    // 获取当前正在绘制的线条
    const currentLine = this.linesGroup.children[this.currentLineIndex];
    if (!(currentLine instanceof Line2)) return;

    // 更新绘制进度
    this.currentDrawProgress += this.options.animation.drawSpeed || 0.01;
    
    // 使用缓动函数使动画更平滑
    const progress = this.easeInOutCubic(this.currentDrawProgress);
    
    // 计算当前应该显示的点数
    const visiblePoints = Math.floor(progress * (this.options.line.pointsPerLine || 100));
    
    // 设置线条可见部分
    currentLine.geometry.setDrawRange(0, visiblePoints);

    // 当前线条绘制完成
    if (this.currentDrawProgress >= 1) {
      setTimeout(() => {
        this.currentLineIndex++;

        // 如果所有线条都绘制完成
        if (this.currentLineIndex >= this.curves.length) {
          // 重置所有线条为不可见
          this.linesGroup.children.forEach(line => {
            if (line instanceof Line2) {
              line.geometry.setDrawRange(0, 0);
            }
          });

          // 延迟一段时间后重新开始
          setTimeout(() => {
            this.currentLineIndex = 0;
            this.currentDrawProgress = 0;
            this.animationId = requestAnimationFrame(this.animate.bind(this));
          }, this.options.animation.delayBeforeRestart);
        } else {
          // 继续绘制下一条线
          this.currentDrawProgress = 0;
          this.animationId = requestAnimationFrame(this.animate.bind(this));
        }
      }, this.options.animation.delayBetweenLines);
    } else {
      // 继续绘制当前线条
      this.animationId = requestAnimationFrame(this.animate.bind(this));
    }
  }

  /**
   * 缓动函数 - 使动画更平滑
   */
  private easeInOutCubic(t: number): number {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /**
   * 将经纬度转换为球面坐标
   * @param radius 地球半径
   * @param longitude 经度
   * @param latitude 纬度
   * @returns 球面坐标
   */
  private lonLatToXYZ(radius: number, longitude: number, latitude: number): THREE.Vector3 {
    // 将经纬度转换为弧度
    const lon = (-longitude * Math.PI) / 180;
    // 将纬度转换为弧度
    const lat = (latitude * Math.PI) / 180;

    return new THREE.Vector3(
      // x: 地球半径 * 纬度 * 经度
      radius * Math.cos(lat) * Math.cos(lon),
      // y: 地球半径 * 纬度
      radius * Math.sin(lat),
      // z: 地球半径 * 纬度 * 经度
      radius * Math.cos(lat) * Math.sin(lon)
    );
  }

  /**
   * 获取向量中心
   * @param v1 向量1
   * @param v2 向量2
   * @returns 向量中心
   */
  private getVectorCenter(v1: THREE.Vector3, v2: THREE.Vector3): THREE.Vector3 {
    return v1.add(v2).divideScalar(2);
  }

  /**
   * 获取比例点，获取两向量之间的任意一向量。
   * 具体是哪一点，由 lerp 的第二个参数决定，范围 0.0 ~ 1.0， 即：
   *  - 0 = 初始点;
   *  - 0.5 = 中间点;
   *  - 1 = 终点;
   * length 与两点的距离归一化为 0.0 ~ 1.0 （理论上）
   *
   * @param v1 向量1
   * @param v2 向量2
   * @param length 长度
   * @returns 比例点
   */
  private getProportionalPoint(v1: THREE.Vector3, v2: THREE.Vector3, length: number): THREE.Vector3 {
    // 两点之间的距离
    const distance = v1.distanceTo(v2);
    /**
     * 线性插值，计算两个点之间的插值点：
     *  - lerp 是 "Linear Interpolation"（线性插值）的缩写
     *
     * 它的工作原理是：
     *  - v1 是起始点
     *  - v2 是终点
     *  - length / distance 是一个 0 到 1 之间的比例值
     *  - length 是我们想要的长度
     *  - distance 是两点之间的实际距离
     *
     *  这个比例决定了新点在 v1 和 v2 之间的位置
     *
     *  举个例子：
     *  - 如果 length/distance = 0.5，新点就在中间（的位置）
     *  - 如果 length/distance = 0.25，新点就在靠近 v1 的四分之一处
     *  - 如果 length/distance = 0.75，新点就在靠近 v2 的四分之一处
     *
     *  在飞线的上下文中：
     *  - 这个方法用于计算贝塞尔曲线的控制点
     *  - 通过调整 length / distance 的比例，可以控制曲线的弧度和形状
     *  - 这是一个在 3D 图形编程中常用的方法，用于在两点之间找到一个按比例分割的点。
     */
    return v1.lerp(v2, length / distance);
  }

  /**
   * 清理
   */
  public dispose(): void {
    this.removeFromScene();
    this.disposeResources();
  }

  /**
   * 从场景中移除
   */
  private removeFromScene(): void {
    this.scene.remove(this.linesGroup);
  }

  /**
   * 清理资源
   */
  private disposeResources(): void {
    this.linesGroup.traverse(object => {
      if (object instanceof THREE.Mesh || object instanceof Line2) {
        object.geometry.dispose();
        object.material.dispose();
      }
    });
  }

  /**
   * 更新飞线动画
   * @param time 当前时间戳
   */
  public update(time: number): void {
    if (this.animationId === null) {
      this.startAnimation();
    }
  }

  /**
   * 重置所有线条
   */
  private resetAllLines(): void {
    this.linesGroup.children.forEach(line => {
      if (line instanceof Line2) {
        line.geometry.setDrawRange(0, 0);
      }
    });
  }

  /**
   * 开始动画
   */
  private startAnimation(): void {
    this.resetAllLines();
    this.currentLineIndex = 0;
    this.currentDrawProgress = 0;
    this.animationId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * 更新分辨率
   */
  public updateResolution(width: number, height: number): void {
    this.linesGroup.traverse((object) => {
      if (object instanceof Line2) {
        const material = object.material as LineMaterial;
        material.resolution.set(width, height);
      }
    });
  }
}
